'use client';

import { useState } from 'react';
import { Box, Container, ButtonBase, Typography } from '@mui/material';
import { useStackNavigation } from '@/hooks/useStackNavigation';
import IconPressable from '@/components/IconPressable';
import MyItemTab from '../MyItemTab/MyItemTab';
import MyCollectionTab from '../MyCollectionTab/MyCollectionTab';
import {
  TEMP_FG_PRIMARY,
  TEMP_FG_INVERSE,
} from '@/constants/tempColors';
import { TEMP_TEXT_SEMIBOLD_L, TEMP_TEXT_SEMIBOLD_XL, TEMP_TITLE_EXTRABOLD_XL } from '@/constants/tempTypo';
import { SAFE_AREA_TOP } from '@/contexts/SafeAreaContext';
import { useTranslator } from '@/lang';
import { useDarkColors } from '@/hooks/useDarkColors';
import { BOTTOM_NAV_HEIGHT } from '@/components/BottomNavigation/BottomNav';

interface MyItemsContentProps {
  initialTab?: number;
  onBackFallback?: () => void;
  isBottomNavMode?: boolean;
}

const TAB_KEYS = ['mypage.my_items', 'mypage.my_collection'];

type ViewMode = 'grid' | 'list';

export default function MyItemsContent({ initialTab = 0, onBackFallback, isBottomNavMode = false }: MyItemsContentProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const { push, pop } = useStackNavigation();
  const { t } = useTranslator();
  const c = useDarkColors();

  const handleBack = () => {
    if (onBackFallback) {
      onBackFallback();
    } else {
      pop();
    }
  };

  return (
    <Container
      maxWidth="sm"
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        bgcolor: c.bg,
      }}
    >
      {/* Header */}
      <Box sx={{ flexShrink: 0, pt: SAFE_AREA_TOP }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 64,
            minHeight: 60,
            py: '14px',
          }}
        >
          {isBottomNavMode ? (
            <Box sx={{ px: '20px' }}>
              <Typography sx={{ ...TEMP_TITLE_EXTRABOLD_XL, color: c.fg }}>
                {t('nav.collection')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ pl: '20px', pr: '4px', py: '6px' }}>
                <IconPressable name="icArrowleft" size={24} color={c.fg} onPress={handleBack} />
              </Box>
            </Box>
          )}

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box sx={{ px: '4px', py: '6px' }}>
                <IconPressable
                  name="icFeed"
                  size={24}
                  color={viewMode === 'grid' ? TEMP_FG_PRIMARY : c.fgLow}
                  onPress={() => setViewMode('grid')}
                />
              </Box>
              <Box sx={{ pl: '4px', pr: '20px', py: '6px' }}>
                <IconPressable
                  name="icList"
                  size={24}
                  color={viewMode === 'list' ? TEMP_FG_PRIMARY : c.fgLow}
                  onPress={() => setViewMode('list')}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tab Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: '20px', flexShrink: 0 }}>
        {TAB_KEYS.map((key, index) => {
          const isActive = index === activeTab;
          return (
            <Box
              key={key}
              onClick={() => setActiveTab(index)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pt: '4px',
                pb: '12px',
                px: '10px',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                borderBottom: isActive ? `1px solid ${c.fg}` : '1px solid transparent',
              }}
            >
              <Typography
                sx={{
                  ...TEMP_TEXT_SEMIBOLD_L,
                  color: isActive ? c.fg : c.fgLow,
                }}
              >
                {t(key)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative', minHeight: 0 }}>
        {activeTab === 0 ? <MyItemTab viewMode={viewMode} /> : <MyCollectionTab />}
      </Box>

      {/* Bottom Button */}
      {!isBottomNavMode && (
        <Box sx={{ px: '20px', pt: '12px', pb: '12px', flexShrink: 0 }}>
          <ButtonBase
            onClick={() => push('/card-gacha')}
            sx={{
              bgcolor: TEMP_FG_PRIMARY,
              borderRadius: '16px',
              py: '18px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
              {t('mypage.draw_items')}
            </Typography>
          </ButtonBase>
        </Box>
      )}
    </Container>
  );
}
